import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { ThemeProvider } from "./theme";
import { PageLoader } from "./components/ui";
import Layout from "./components/Layout";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Dashboard from "./pages/Dashboard";
import ProfileNew from "./pages/ProfileNew";
import ProfileDetail from "./pages/ProfileDetail";
import History from "./pages/History";
import ScanDetail from "./pages/ScanDetail";
import Alerts from "./pages/Alerts";
import Account from "./pages/Account";
import Admin from "./pages/Admin";

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function RequireSubscription({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.has_access) return <Navigate to="/pricing" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoader />;
  if (user) return <Navigate to={user.has_access ? "/dashboard" : "/pricing"} replace />;
  return children;
}

function Root() {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoader />;
  if (!user) return <Landing />;
  return <Navigate to={user.has_access ? "/dashboard" : "/pricing"} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
            <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />

            <Route path="/dashboard" element={<RequireSubscription><Dashboard /></RequireSubscription>} />
            <Route path="/profiles/new" element={<RequireSubscription><ProfileNew /></RequireSubscription>} />
            <Route path="/profiles/:id" element={<RequireSubscription><ProfileDetail /></RequireSubscription>} />
            <Route path="/history" element={<RequireSubscription><History /></RequireSubscription>} />
            <Route path="/scans/:id" element={<RequireSubscription><ScanDetail /></RequireSubscription>} />
            <Route path="/alerts" element={<RequireSubscription><Alerts /></RequireSubscription>} />
            <Route path="/account" element={<RequireAuth><Layout><Account /></Layout></RequireAuth>} />
            <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
